/**
 * Deciding whether the phone's relayed picture is still worth believing.
 *
 * Split out of the panel because it is the only part that can be wrong in a
 * way nobody notices. The rendering is obvious on sight; this is not, and the
 * first version of it was broken in exactly the way that mattered.
 *
 * **The trap.** The frame is stored on the server, not streamed. When the
 * phone stops publishing — it slipped, the screen locked, Wi-Fi dropped, the
 * browser throttled its timers in the background — the server goes on serving
 * the last frame indefinitely, with a 200, forever. So a panel that infers
 * freshness from "the request succeeded" stays confidently green while the
 * student aligns their phone against a photograph of where it used to point,
 * then sits a three-hour exam aimed at the ceiling. That is the precise
 * failure the panel was built to prevent, so it is the one it must not commit.
 *
 * Freshness therefore comes only from the FRAME:
 *
 *  - `serverAgeMs` — how old the newest frame is, as stamped by the server.
 *    Exact, but travels in a custom header, so it is only readable
 *    cross-origin while the API's CORS `exposeHeaders` names it. If that ever
 *    drifts, this returns null and the check below carries on alone.
 *  - `changedAtMs` — when the bytes last actually CHANGED locally, tracked via
 *    the ETag. Needs no cooperation from CORS, and covers a phone that goes
 *    quiet while the panel is open.
 *
 * The older of the two wins, so losing either signal degrades the panel rather
 * than blinding it.
 */

/**
 * Past this, the picture stops being evidence of anything current.
 *
 * Set well above the phone's ~700 ms publish interval so an ordinary slow
 * frame does not cry wolf: this should fire when the phone has stopped
 * sending, not when it is merely behind.
 */
export const STALE_AFTER_MS = 6000;

export interface FreshnessInput {
  /** Local time the displayed bytes last changed; null before any frame. */
  changedAtMs: number | null;
  /** Server-reported age of the newest frame, or null if unreadable. */
  serverAgeMs: number | null;
  nowMs: number;
}

export interface Freshness {
  /** No frame has ever arrived. */
  waiting: boolean;
  /** A frame is shown but is too old to be trusted as current. */
  stale: boolean;
  /** Best estimate of the displayed frame's age; null while waiting. */
  ageMs: number | null;
}

export function viewfinderFreshness({
  changedAtMs,
  serverAgeMs,
  nowMs,
}: FreshnessInput): Freshness {
  if (changedAtMs === null) {
    return { waiting: true, stale: false, ageMs: null };
  }

  const localAge = Math.max(0, nowMs - changedAtMs);
  // The older of the two. The local clock catches a phone that stopped while
  // the panel was open; the server's age catches one that had already frozen
  // before the panel mounted, when the first frame to arrive is already old.
  const ageMs = Math.max(localAge, serverAgeMs ?? 0);

  return { waiting: false, stale: ageMs > STALE_AFTER_MS, ageMs };
}
